import React, { useState } from "react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { TimePicker,DatePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import { deleteicon, save } from "./svg";
import ToastMessage from "../../utils/mui/righttoptoast";
import api from "../../api/api";
import ClockModal from "./clockmodal";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ManualEntry from "../employeeTimesheet/manualentry";
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import Swal from "sweetalert2";
import { getPracticeId } from "../../config/practice";
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const TimesheetTable = ({
  data,
  setIsNewApp,
  isNewApp,
  onEdit,
  selectedRows,
  setSelectedRows,
  loading,
  fetchtimesheetdata,
  onDelete,
  selected,
  start_date,
}) => {
  console.log("TimesheetTable Rendered with data:", data);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [editedRows, setEditedRows] = useState({});
  const [invalidFields, setInvalidFields] = useState({});
  const practiseid = getPracticeId();
  const [showModal, setShowModal] = useState(false);
  const [selectedrow, setSelectedRow] = useState(null);

  const [shiftFilter, setShiftFilter] = useState("All");
  // Toast state
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState("info");
  const [alertmessage, setAlertMessage] = useState("");

  const [openManualEntryModal, setOpenManualEntryModal] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [editRows, setEditRows] = useState([]);

  const handlealert = () => setOpen(false);

  const filteredData = data.filter((emp) => {
    if (shiftFilter === "All") return true;

    if (!emp.clock_in_time) return false;

    const hour = dayjs(emp.clock_in_time).hour();

    if (shiftFilter === "Morning") return hour < 12;
    if (shiftFilter === "Afternoon") return hour >= 12;

    return true;
  });

  const totalEntries = filteredData.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const handlePrevious = () =>
    currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNext = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handleRowsChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  // Toast helper
  const showToast = (message, type = "info") => {
    setAlertMessage(message);
    setSeverity(type);
    setOpen(true);
  };

  // Calculate total hours
  const calculateTotalHours = (clockIn, clockOut, breakMinutes) => {
    if (!clockIn || !clockOut) return "—";
    const inTime = dayjs(clockIn instanceof Date ? clockIn : new Date(clockIn));
    const outTime = dayjs(
      clockOut instanceof Date ? clockOut : new Date(clockOut),
    );
    if (!inTime.isValid() || !outTime.isValid()) return "—";

    let diffMinutes =
      outTime.diff(inTime, "minute") - (Number(breakMinutes) || 0);
    if (diffMinutes < 0) return "—";

    const dur = dayjs.duration(diffMinutes, "minutes");
    const hours = Math.floor(dur.asHours());
    const minutes = dur.minutes();
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  // Validation
  const validateTimeRules = (id, updatedFields) => {
    const emp = data.find((d) => d.id === id);
    if (!emp) {
      console.error("Row not found for id:", id);
      return false;
    }
    const existing = editedRows[id] || {};

    const clockIn =
      updatedFields.clock_in_time ??
      existing.clock_in_time ??
      emp.clock_in_time;
    const clockOut =
      updatedFields.clock_out_time ??
      existing.clock_out_time ??
      emp.clock_out_time;
    const breakMinutes = Number(
      updatedFields.break_minutes ??
        existing.break_minutes ??
        emp.break_minutes ??
        0,
    );

    setInvalidFields((prev) => ({
      ...prev,
      [id]: {
        clockInMissing: !clockIn,
        clockOutMissing: !clockOut,
      },
    }));

    if (!clockIn || !clockOut) return true;

    const inTime = dayjs(clockIn instanceof Date ? clockIn : new Date(clockIn));
    const outTime = dayjs(
      clockOut instanceof Date ? clockOut : new Date(clockOut),
    );
    if (!inTime.isValid() || !outTime.isValid()) return true;

    const diffMinutes = outTime.diff(inTime, "minute");

    if (diffMinutes <= 0) {
      showToast("Clock-out must be after Clock-in.", "warning");
      return false;
    }
    if (diffMinutes > 24 * 60) {
      showToast(
        "Clock-in and Clock-out cannot exceed 24 hours difference.",
        "warning",
      );
      return false;
    }
    if (breakMinutes > diffMinutes) {
      showToast("Break time cannot exceed total working duration.", "warning");
      return false;
    }

    return true;
  };

  // Commit change
  const commitRowValue = (id, field, value) => {
    setEditedRows((prev) => {
      const existing = prev[id] || {};
      const updated = { ...existing, [field]: value };
      return { ...prev, [id]: updated };
    });
  };
  const convertToUTC = (time) => {
    if (!time) return time;

    return dayjs
      .tz(time, "America/Regina") // convert local (Regina) time
      .utc() // to UTC
      .format("YYYY-MM-DDTHH:mm:ss");
  };

  // ✅ Handle Save with ISO UTC timestamps
  const handleSave = async (id, empid) => {
    console.log("💾 Save clicked for ID:", id, "EmpID:", empid);
    if(id === undefined || id === "" || id === null){ 
      showToast("Employee ID is missing. Cannot save.", "error");
      return;
    }
    const emp = data.find((d) => d.id === id);
    const row = editedRows[id] || {};

    const clockIn = row.clock_in_time || emp.clock_in_time;
    const clockOut = row.clock_out_time || emp.clock_out_time;
    // const breakMinutes = row.break_minutes ?? emp.break_minutes ?? 0;

    if (!clockIn || !clockOut) {
      showToast(
        "Clock In and Clock Out are required before saving.",
        "warning",
      );
      setInvalidFields((prev) => ({
        ...prev,
        [id]: { clockInMissing: !clockIn, clockOutMissing: !clockOut },
      }));
      return;
    }

    // Build payload with ISO UTC times
    const payload = {};
    if (row.clock_in_time)
      payload.clock_in_time = isNewApp
        ? row.clock_in_time
        : convertToUTC(row.clock_in_time);
    if (row.clock_out_time)
      payload.clock_out_time = isNewApp
        ? row.clock_out_time
        : convertToUTC(row.clock_out_time);

    if (
      row.break_minutes !== undefined &&
      row.break_minutes !== emp.break_minutes
    )
      payload.break_minutes = Number(row.break_minutes);

    if (Object.keys(payload).length === 0) {
      showToast("No changes to save.", "info");
      return;
    }

    payload.uu_id = emp.uu_id;
    payload.emp_id = emp.emp_id;
    payload.emp_name = emp.emp_name;
    payload.work_date = emp.work_date;
    console.log("🛰️ Sending payload:", payload);
    try {
      const response = await api.post(
        `/v1/${practiseid}/timesheet/save-clockin-checkout`,
        payload,
      );

      if (response.status === 200) {
        console.log("✅ API response:", response.data);
        showToast("Saved successfully!", "success");
        fetchtimesheetdata();
        setEditRows((prev) => prev.filter((empid) => empid !== id));
      } else {
        showToast(
          response?.message || "Failed to save. Please try again.",
          "error",
        );
      }
    } catch (error) {
      console.error("❌ Save failed:", error);
      showToast("Network or server error. Please try again.", "error");
    }

    onEdit(id, row);
    setEditedRows((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    setInvalidFields((prev) => ({
      ...prev,
      [id]: { clockInMissing: false, clockOutMissing: false },
    }));
  };
  // const handleHeaderCheckbox = () => {
  //   if (selectedRows.length === currentData.length) {
  //     // Uncheck all
  //     setSelectedRows([]);
  //   } else {
  //     // Select all current page rows
  //     setSelectedRows(
  //       currentData
  //         .filter((emp) => emp.status !== "Submitted")
  //         // .map((emp) => emp.id),
  //         .map(emp => `${emp.empid}-${emp.work_date}-${emp.period}`) 
  //     );
  //   }
  // };
  const handleHeaderCheckbox = () => {
    if (selectedRows.length === currentData.length) {
      // Uncheck all
      setSelectedRows([]);
    } else {
      // Select all current page rows
      setSelectedRows(
        currentData
          .filter((emp) => emp.status !== "Submitted")
          .map((emp) => emp.id),
      );
    }
  };
  const handleRowCheckbox = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };
  const getShiftLabel = (clockIn) => {
    if (!clockIn) return null;

    const time = dayjs(clockIn);
    if (!time.isValid()) return null;

    const hour = time.hour();

    if (hour < 12) return "Morning";
    return "Afternoon";
  };


  const handleOpenManualEntryModal = (empid) => {
    setSelectedEmpId(empid);
    setOpenManualEntryModal(true); // Open modal
  };

  const handleCloseManualEntry = () => {
    setOpenManualEntryModal(false); // Close modal
    setSelectedEmpId(null);
  };



  // const handleSubmit = async ({ status, ids }) => {
  //  if (!Array.isArray(ids)) ids = [ids];
  //   console.log("Submitting with status:", status, "IDs:", ids);
  //     try {
  //       // setLoading(true);
  //       const response = await api.put(
  //         `/v1/${practiseid}/timesheets/status-update?status=${status}`,
  //         {
  //           timesheet_ids: ids,
  //         },
  //       );
  //       if (response.status == 200) {
  //         setSeverity("success");
  //         setAlertMessage(response?.message || "Timesheet Updated Sucessfully");
  //         setOpen(true);
  //         fetchtimesheetdata();
  //       } else {
  //         setSeverity("info");
  //         setAlertMessage(response?.message || "Something went wrong");
  //         setOpen(true);
  //       }
  //     } catch (error) {
  //       console.log(error);
  //     } finally {
  //       setSelectedRows([]);
  //       // setLoading(false);
  //     }
  //   };




const handleSubmit = async ({ status, ids }) => {
  if (!Array.isArray(ids)) ids = [ids];

  const result = await Swal.fire({
    title: "Are you sure?",
    text: `Update ${ids.length} timesheet(s) to "${status}"?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, update it!",
    cancelButtonText: "Cancel",
    customClass: {
      confirmButton: "bg-green-600 text-white px-4 py-2 m-1 rounded hover:bg-green-700",
      cancelButton: "bg-gray-300 text-black px-4 py-2 m-1 rounded hover:bg-gray-400",
    },
    buttonsStyling: false,
  });

  if (result.isConfirmed) {
    try {
      const response = await api.put(
        `/v1/${practiseid}/timesheets/status-update?status=${status}`,
        { timesheet_ids: ids }
      );
      if (response.status === 200) {
        // Swal.fire("Success!", response?.message || "Timesheet Updated Successfully", "success");
        setSeverity("success");
        setAlertMessage(response?.message || "Timesheet Updated Sucessfully");
        setOpen(true);
        fetchtimesheetdata();
      } else {
        // Swal.fire("Oops!", response?.message || "Something went wrong", "info");
          setSeverity("info");
          setAlertMessage(response?.message || "Something went wrong");
          setOpen(true);
      }
    } catch (error) {
      console.log(error);
      // Swal.fire("Error!", "An unexpected error occurred.", "error");
    } finally {
      setSelectedRows([]);
    }
  }
};


  // let renderedEmpIds = null;
  return (
    <>
      <div className="flex justify-between items-center mt-2 mb-2">
        {/* <div className="flex items-center   gap-2">
          <label className="text-sm  mt-2 text-center font-medium text-gray-700">
            Shift:
          </label>

          <select
            value={shiftFilter}
            onChange={(e) => {
              setShiftFilter(e.target.value);
              setCurrentPage(1); // reset to first page when filtering
            }}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md"
          >
            <option value="All">All</option>
            <option value="Morning">Morning</option>
            <option value="Afternoon">Afternoon</option>
          </select>
        </div> */}
        {/* <div className="flex items-center gap-3 h-6">
  <span className="text-sm text-gray-600 flex items-center mb-2">Old App</span>

  <label className="relative inline-flex items-center cursor-pointer">
    <input
      type="checkbox"
      className="sr-only peer"
      checked={isNewApp}
      onChange={(e) => setIsNewApp(e.target.checked)}
    />


    <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-[#1D2951] transition-colors duration-300"></div>


    <span className="absolute left-0.5 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-md transition-transform duration-300 peer-checked:translate-x-5"></span>
  </label>

  <span className="text-sm text-gray-600 flex items-center mb-2">New App</span>
</div> */}
      </div>
      <div className=" bg-white rounded-lg">
        <div className="overflow-x-auto">

        

        <div className="space-y-4">

  {/* Header */}
  <div className="bg-gray-100 p-2 rounded-lg flex items-center gap-2 border border-gray-200">
    <div className="w-48 text-center font-semibold text-gray-700">
  {currentData.some((emp) => emp.status !== "Submitted") ? (
    <>
      <input
        type="checkbox"
        checked={
          currentData.filter(
            (emp) => emp.status !== "Submitted",
          ).length > 0 &&
          selectedRows.length ===
            currentData.filter(
              (emp) => emp.status !== "Submitted",
            ).length
        }
        onChange={handleHeaderCheckbox}
      />
      <span className="ml-2">Employee Name</span>
    </>
  ) : (
    <span>Employee Name</span>
  )}</div>
    <div className="flex-1 p-2 flex flex-col sm:flex-row justify-between items-center gap-2">
      <div className="w-24 font-semibold text-gray-700">Period</div>
      <div className="flex-1 text-center font-semibold text-gray-700">Clock In</div>
      <div className="flex-1 text-center font-semibold text-gray-700">Clock Out</div>
      {/* <div className="w-20 text-center font-semibold text-gray-700">Break (min)</div> */}
      <div className="w-28 text-center font-semibold text-gray-700">Total Hours</div>
      <div className="w-32 text-center font-semibold text-gray-700">Actions</div>
    </div>
  </div>

  {!loading && currentData.length === 0 ? (
    <div className="text-center py-8 text-gray-500 text-sm font-medium">
      No data available
    </div>
  ) : (
    // Group by empid
    Object.values(
      currentData.reduce((acc, item) => {
        if (!acc[item.empid]) acc[item.empid] = { ...item, periods: [] };
        acc[item.empid].periods.push(item);
        return acc;
      }, {})
    ).map((emp) => {
      const config = emp.config ? JSON.parse(emp.config) : {};
      return (
        <div
          key={emp.empid}
          className="bg-white shadow-md rounded-lg flex border border-gray-200 overflow-hidden"
        >
          {/* Left: Employee Info */}
          <div className="w-48 p-2 flex flex-col items-center text-center">
            <img
              src={config?.profilepic || "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAM1BMVEXk5ueutLeqsbTn6eqpr7PJzc/j5ebf4eLZ3N2wtrnBxsjN0NLGysy6v8HT1tissra8wMNxTKO9AAAFDklEQVR4nO2d3XqDIAxAlfivoO//tEOZWzvbVTEpic252W3PF0gAIcsyRVEURVEURVEURVEURVEURVEURVEURVEURVEURflgAFL/AirAqzXO9R7XNBVcy9TbuMHmxjN6lr92cNVVLKEurVfK/zCORVvW8iUBnC02dj+Wpu0z0Y6QlaN5phcwZqjkOkK5HZyPAjkIjSO4fIdfcOwFKkJlX4zPu7Ha1tIcwR3wWxyFhRG6g4Je0YpSPDJCV8a2Sv2zd1O1x/2WMDZCwljH+clRrHfWCLGK8REMiql//2si5+DKWKcWeAGcFMzzNrXC/0TUwQ2s6+LhlcwjTMlYsUIQzPOCb7YBiyHopyLXIEKPEkI/TgeuiidK/R9FniUDOjRDpvm0RhqjMyyXNjDhCfIMYl1gGjIMIuYsnGEYRMRZOMMunaLVwpWRW008v6fYKDIzxCwVAeNSO90BJW6emelYBRF/kHpYGVaoxTDAaxOFsfP9y8hpJ4xd7gOcij7JNGQ1EYFgkPJa1jQEiYZXRaRINKxSDUW9n+FT82lSKadkiru9/4XPqSLWOekGPoY05TAvLm9orm+YWuwHoBHkZKijNBJGmeb61eL6Ff/6q7bLr7yvv3vKGhpDRjvgjGaPz+gUg6YgcvpyAR2FIZ9U6nEEyZRTovmEU32KichpGn7C17XrfyH9gK/c0CMP05HZIM2uf9sEveizKveBy9/6Qt7o89ne33D525cfcIMW6ab+TMEukQbQbu+xu7X3A9bChmWaCeAkG17bpntwXgWxHaMzGPmUaR5dQZiKqRVeUZ3047fi3nAu28h4CHxCsZAgmEH8Y27jJAhm8c+5RQzRQNVGhVFSfxOYIjp/pP7RxzjevYXVGf4eLt+BJ1vCuLuLkrgABgCGXZ2wik5uty+oBvNirI6mkzhAf4Gsb58Hcm67Jzd+KwD10BYPLL3e0MjvKrgAULnOfveF/O4N2Xb9BZom3gJes3F9X5Zze8/6Yt09b4CrqsEjUv8oFBaR2rl+6CZr2xVrp24o/WitBKuGrrpl1+bFkmK2qXTON4VpbdfLa7o7y/WdLxG7lm2Lqh2clOwTegbvc/vj2U78CwhA87Bn8G5Nk3eOb0Nsr9flz3sG78UUtue4kpv1xvjg3TMay62BMlTlP+vrOMnJsRmt/ze0jsfkPPYdAH57hK+34PeOyc8XIXu5xT2HsUkdZz+adwg8HGFfQ3K5jtDvbUiO4Di9/ywHGrL88pDizZ++oTp+an+SMX/ndymUCwmHMdO7yuOx83pUx/eEMU0AvxWndwgidAqOZ8ypCwdEfvvEo6D9HwpA8wzvmOJEqAg9ySu8g4x0Hb9hSB/BANEKJ+LbPBU0lzbAJs4xt1AoshKkUGQmiH8/jJ0gdhTTLmSegHlPE0oOdXALnqDjKYh3px//fSgSWG8UqfrrIICzYYSJXRr9BSPbpNzw7gBjKjKOYI7ReIGqQRIap5+5MdjyvuDkExvGeXSlONWZAP3/AZBwJohU7QJRGU+cTVH18ELmRPNBmibW6MT/k1b0XhdkRBvyT6SB6EYv/GvhSmRNpGngRULsAlxMCGNXp7w3FfdEbTEEDdLI9TdIKRUzUesa3I461ER8cpNT7gMRhpKmYVS9ELOgCUQsa4SsulciKiLbY+AnHD8cpuhISsnxpamI84sbDq9qYJgf8wiiOBrC7Ml7M7ZECCqKoiiKoiiKoiiKoijv5AvJxlZRyNWWLwAAAABJRU5ErkJggg=="}
              alt={emp.firstname}
              className="w-20 h-20 rounded-full border-2 border-red-500 object-cover"
            />
            <span className="font-semibold text-gray-900">{emp.firstname} {emp.lastname}</span>
            <span className="text-sm text-gray-500">#{emp.empid}</span>
            <span className="text-sm text-gray-500">#{emp?.user_role || emp?.role}</span>
          </div>

          {/* Right: Period Rows */}
  {/* Right: Period Rows */}
  <div className="flex-1 p-4 flex flex-col gap-2">
    {(() => {
      // All clock-in/out sessions for the day, not just one Morning + one
      // Afternoon slot — extra sessions were silently hidden by the old
      // fixed-two-row render (feature request 2026-09-08). The backend
      // already returns every timesheet row; render them all, ordered by
      // clock-in, labelled by shift + sequence. Employees with no entries
      // get one placeholder row so manual entry is still reachable.
      const sessions = (emp.periods || [])
        .filter((p) => p.id)
        .sort((a, b) => new Date(a.clock_in_time || 0) - new Date(b.clock_in_time || 0));
      const rowsToRender = sessions.length ? sessions : [{}];
      return rowsToRender.map((periodData, sessionIdx) => {
      const periodName = periodData.id
        ? `${getShiftLabel(periodData.clock_in_time) || "Session"}${
            sessions.length > 1 ? ` ${sessionIdx + 1}` : ""
          }`
        : "No entries";
      const isEditing = editRows.includes(periodData.id);
      const edited = editedRows[periodData.id] || {};
      const clockInISO = edited.clock_in_time ?? periodData.clock_in_time ?? null;
      const clockOutISO = edited.clock_out_time ?? periodData.clock_out_time ?? null;
      const breakMinutes = Number(edited.break_minutes ?? periodData.break_minutes ?? 0 );

      // const periodData = emp.periods.find(p => p.period === periodName) || {};
      // const clockInISO = periodData.clock_in_time ?? null;
      // const clockOutISO = periodData.clock_out_time ?? null;
      // const breakMinutes = Number(periodData.break_minutes ?? 0);
      const totalHours = calculateTotalHours(clockInISO, clockOutISO, breakMinutes);
      const invalid = invalidFields[periodData.id] || {};
      const auditLogs = periodData.audit_logs ? JSON.parse(periodData.audit_logs) : {};
      const isStatus = periodData.status ?? null;


      return (
        <div
          key={periodData.id || periodName}
          className="p-2 rounded-lg flex flex-col sm:flex-row justify-between items-center gap-2 border-b border-gray-100"
        >
          {/* Period data */}

          {periodData.id && periodData?.status !== "Submitted" && clockInISO && clockOutISO ? (
              <input
                type="checkbox"
                checked={selectedRows.includes(periodData?.id)}
                onChange={() => handleRowCheckbox(periodData?.id)}
              />
            ) : (             
              <span className="m-1">&nbsp;</span>
            )}
            {clockInISO ? (            
              <div>
                <FontAwesomeIcon
                  onClick={() => {
                    setSelectedRow(periodData);
                    setShowModal(true);
                  }}
                  icon={faUser}
                  className=" text-lg cursor-pointer"
                />
              </div>
            ) : (             
              <span className="m-1">&nbsp;</span>
            )}
          
          {/* Period Label */}
          <div className="w-24 font-medium text-gray-700">{periodName}</div>

          {/* Clock In */}
          <div className="flex-1">
            <TimePicker
              format="hh:mm aa"
              showMeridiem
              cleanable={false}
              value={clockInISO ? new Date(clockInISO) : null}
               disabled={!periodData.id || !isEditing}
              onChange={(value) => {
  if (!value) return;

  let clockIn = dayjs(value);

  // ✅ Preserve original date
  if (clockInISO) {
    const existingDate = dayjs(clockInISO);

    clockIn = clockIn
      .year(existingDate.year())
      .month(existingDate.month())
      .date(existingDate.date());
  }

  const iso = clockIn.format("YYYY-MM-DDTHH:mm:ss");

  if (
    validateTimeRules(periodData.id, {
      clock_in_time: iso,
    })
  ) {
    commitRowValue(
      periodData.id,
      "clock_in_time",
      iso
    );

    setInvalidFields((prev) => ({
      ...prev,
      [periodData.id]: {
        ...prev[periodData.id],
        clockInMissing: false,
      },
    }));
  }
}}
              className={`w-[90%]] ${invalid.clockInMissing ? "border-2 border-red-500 rounded-md" : ""}`}
            />
          </div>

          {/* Clock Out */}
          <div className="flex-1 relative">
            {auditLogs?.autoClockout === "Y" ? (
              <DatePicker
                format="dd-MM-yyyy hh:mm aa"
                showMeridiem
                cleanable={false}
                value={clockOutISO ? new Date(clockOutISO) : null}
                 disabled={!periodData.id || !isEditing}
                onChange={(value) => {
  if (!value) return;

  let clockOut = dayjs(value);

  // ✅ Preserve existing clock-out date
  if (clockOutISO) {
    const existingDate = dayjs(clockOutISO);

    clockOut = clockOut
      .year(existingDate.year())
      .month(existingDate.month())
      .date(existingDate.date());
  }

  // ✅ Use latest edited clock-in
  const clockIn = clockInISO ?? null;

  // ✅ Handle overnight shift
  if (clockIn && clockOut.isBefore(dayjs(clockIn))) {
    clockOut = clockOut.add(1, "day");
  }

  const iso = clockOut.format("YYYY-MM-DDTHH:mm:ss");

  if (
    validateTimeRules(periodData.id, {
      clock_out_time: iso,
    })
  ) {
    commitRowValue(
      periodData.id,
      "clock_out_time",
      iso
    );

    setInvalidFields((prev) => ({
      ...prev,
      [periodData.id]: {
        ...prev[periodData.id],
        clockOutMissing: false,
      },
    }));
  }
}}
                className={`w-[90%] ${invalid.clockOutMissing ? "border-2 border-red-500 rounded-md" : ""}`}
              />
            ) : (
              <TimePicker
                format="hh:mm aa"
                showMeridiem
                cleanable={false}
                value={clockOutISO ? new Date(clockOutISO) : null}
                 disabled={!periodData.id || !isEditing}
                onChange={(value) => {
                  if (!value) return;
                  let clockOut = dayjs(value);
                  const clockIn = periodData.clock_in_time ?? null;
                  if (clockIn && clockOut.isBefore(dayjs(clockIn))) clockOut = clockOut.add(1, "day");
                  const iso = clockOut.format("YYYY-MM-DDTHH:mm:ss");
                  if (validateTimeRules(periodData.id, { clock_out_time: iso })) {
                    commitRowValue(periodData.id, "clock_out_time", iso);
                  }
                }}
                className={`w-[90%] ${invalid.clockOutMissing ? "border-2 border-red-500 rounded-md" : ""}`}
              />
            )}
            {auditLogs?.autoClockout === "Y" && (
              <span className="text-red-600 text-xs mt-1 block absolute top-full left-1/2 transform -translate-x-1/2">
                Auto Clockout
              </span>
            )}
          </div>

          {/* Break Minutes */}
          {/* <div className="w-20 text-center">
            <input
              type="number"
              value={breakMinutes}
              min="0"
              onChange={(e) => {
                const numeric = Number(e.target.value);
                if (validateTimeRules(emp.empid, { break_minutes: numeric })) {
                  commitRowValue(emp.empid, "break_minutes", numeric);
                } else {
                  e.target.value = breakMinutes;
                }
              }}
              className="w-full text-center border border-gray-300 rounded-md text-sm p-1"
            />
          </div> */}

          {/* Total Hours */}
          <div className="w-28 text-center font-semibold">{totalHours}</div>

          {/* Actions */}
          <div className="flex gap-1 items-center">
            {isStatus === "Deleted" ? (
              <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-red-100 text-red-700 font-medium text-sm">
                Deleted
              </span>
            ) : isStatus === "Submitted" ? (
              <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-green-200 text-green-600 font-medium text-sm">
                Submitted
              </span>
            ) : periodData.id ? (
              <>
                {/* <button
                  onClick={() => handleSave(periodData.id, emp.empid)}
                  className="text-[#1D2951] hover:text-[#50869A] transition-colors m-1 rounded px-2"
                >
                  {save}
                </button> */}

<div className="flex gap-2 items-center">
  {/* Edit button: show only if NOT editing */}
  {!isEditing && periodData.id && periodData.status !== "Submitted" && (
    <button
      onClick={() => setEditRows(prev => [...prev, periodData.id])}
      className="text-blue-600 hover:text-blue-800 px-1 py-1 rounded"
    >
      <FontAwesomeIcon icon={faPencil} /> {/* Pencil icon for edit */}
    </button>
  )}

  {/* Save button: show only if editing */}
  {isEditing && (
    <button
      onClick={() => handleSave(periodData.id, emp.empid)}
      className="text-green-600 hover:text-green-800 px-1 py-1 rounded"
    >
     {save} {/*<FontAwesomeIcon icon={faSave} /> {/* Save icon */}
    </button>
  )}

  {/* Optional Submit button: only after saving (if you want separate flow) */}
  {!isEditing && periodData.id && periodData.status !== "Submitted" && clockInISO && clockOutISO && (
    <button
      onClick={() =>
        handleSubmit({ status: "Submitted", ids: periodData.id })
      }
      className="text-indigo-600 hover:text-indigo-800 px-1 py-1 rounded"
    >
      {save}
    </button>
  )}
</div>

                <button
                  onClick={() => onDelete({ status: "Deleted", ids: [periodData.id] })}
                  className="text-red-600 hover:text-red-800 transition-colors m-1 rounded px-2"
                >
                  {deleteicon}
                </button>
              </>
            ) : (
              <>
              <ControlPointIcon
                className="m-2 cursor-pointer"
                onClick={() => handleOpenManualEntryModal(emp.empid)}
                titleAccess="Create Manual Entry"
              />
              <div className="p-1 mx-3">&nbsp;&nbsp;</div>
              </>
            )}
          </div>
        </div>
      );
      });
    })()}
  </div>
        </div>
      );
    })
  )}
</div>





        


        


        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center p-4 text-sm text-gray-600">
        <span>
          Showing {startIndex + 1} -{" "}
          {Math.min(startIndex + rowsPerPage, totalEntries)} of {totalEntries}{" "}
          entries
        </span>

        <div className="flex items-center gap-1 mt-2 sm:mt-0">
          <select
            value={rowsPerPage}
            onChange={handleRowsChange}
            className="px-2 py-1 text-xs border border-gray-300 rounded-md"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
          </select>
          <span className="mx-2">per page</span>

          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>

          <span className="px-3 py-1 rounded bg-[#1D2951] text-white">
            {currentPage}
          </span>

          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

        {/* Conditionally render the ManualEntry modal */}
        {openManualEntryModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
            <div className="relative w-full max-w-md bg-white rounded-lg shadow-lg">
              <div className="">
                <div className="bg-[#E179545E] p-2 py-3">
                  <div className="w-full flex items-center justify-between px-2">
                    <span className="ml-2 font-semibold text-[#1D2951] text-base">
                       Manual Entry
                    </span>
                    <button 
                      className="text-[#1D2951] hover:text-gray-700 text-xl leading-none mr-1" 
                      onClick={handleCloseManualEntry}
                    >
                      ✕
                    </button>
                  </div>
                </div>
                
                <ManualEntry 
                closeManualEntryModal={handleCloseManualEntry} 
                fetchtimesheetdata={fetchtimesheetdata} start_date={start_date} empid={selectedEmpId}
                /> {/* Pass close function */}
              </div>
            </div>
          </div>
        )}


      <ClockModal
        open={showModal}
        handleClose={() => setShowModal(false)}
        obj={selectedrow}
      />
      {/* Toast */}
      <ToastMessage
        message={alertmessage}
        severity={severity}
        open={open}
        handleClose={handlealert}
      />
    </>
  );
};

export default TimesheetTable;
